import React, { useState } from 'react';
import Button from '../Button';
import Input from '../Input';

interface InlineEditTagsProps {
  value: string[] | undefined;
  onSave: (value: string[]) => Promise<void> | void;
  onCancel?: () => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  label?: string;
}

const InlineEditTags: React.FC<InlineEditTagsProps> = ({
  value = [],
  onSave,
  onCancel,
  disabled = false,
  className = '',
  placeholder = 'Add a tag',
  label,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tags, setTags] = useState<string[]>(value || []);
  const [newTag, setNewTag] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    if (disabled) return;
    setTags(value || []);
    setIsEditing(true);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(tags);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving tags:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTags(value || []);
    setIsEditing(false);
    onCancel?.();
  };

  if (isEditing) {
    return (
      <div className={`inline-edit-wrapper ${className}`}>
        <div className="animate-slide-down">
          {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
          <div className="flex flex-wrap gap-2 mb-2">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => handleRemoveTag(tag)}
                  className="text-blue-600 hover:text-blue-800"
                  aria-label={`Remove ${tag}`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
              placeholder={placeholder}
              className="flex-1"
            />
            <Button type="button" size="sm" onClick={handleAddTag}>
              Add
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2 animate-fade-in">
          <Button type="button" size="sm" onClick={handleSave} isLoading={isSaving} disabled={isSaving}>
            Save
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-edit-display group ${className}`}>
      <div className="inline-edit-content">
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-gray-400 italic">No tags</span>
        )}
      </div>
      {!disabled && (
        <button
          type="button"
          onClick={handleStartEdit}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2"
          aria-label="Edit tags"
        >
          <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default InlineEditTags;

